import { useEffect, useMemo, useRef, useState } from 'react'
import { loadStripe, Stripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'

import { useLocation } from 'react-router-dom'
import Payment from '../components/Payment'
import { getSharedConfig } from '../get-config'
import useOrder from '../data/useOrder'
import {
  PaymentOrderDocument,
  PaymentOrderQuery,
  SetupRecurringPaymentMethodDocument,
  SetupRecurringPaymentMethodMutation,
  SetupRecurringPaymentMethodMutationVariables,
} from '../graphql'
import SkeletonPayment from '../components/Payment/Skeletons/SkeletonPayment'
import useMutationWithErrorHandling from '../data/useMutationWithErrorHandling'
import getPhoenixUrlParams from '../lib/get-phoenix-url-params'
import sessionUtils from '../data/session-utils'
import { useIntl } from '../localization/react'
import { CashPayment } from '../components/Payment/CashPayment'
import { BlockingError } from '../lib/custom-errors'

interface CustomPaymentMethod {
  id: string
  options: { type: 'static'; subtitle?: string }
}

interface Props {
  stripePromise: Promise<Stripe | null>
  clientSecret?: string
  customPaymentMethods?: Array<CustomPaymentMethod>
  customerSessionClientSecret?: string
}

const PaymentScreen = ({
  stripePromise,
  clientSecret,
  customPaymentMethods = [],
  customerSessionClientSecret,
}: Props) => (
  <Elements
    stripe={stripePromise}
    options={{
      ...(clientSecret && { clientSecret }),
      customPaymentMethods,
      customerSessionClientSecret,
      loader: 'always',
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: '#198073',
        },
      },
    }}
  >
    <Payment />
  </Elements>
)

const PaymentScreenWrapper = () => {
  const intl = useIntl()
  const location = useLocation()
  const [clientSecret, setClientSecret] = useState<string | undefined>()
  const lastFetchedPaymentId = useRef<string | null>(null)
  const { paymentInProgress } = getPhoenixUrlParams(location.search)

  const { orderData, isOrderLoading } = useOrder<PaymentOrderQuery>(PaymentOrderDocument)
  const [setupRecurringPayment, { data: recurringPaymentData, loading: isSetupLoading }] = useMutationWithErrorHandling<
    SetupRecurringPaymentMethodMutation,
    SetupRecurringPaymentMethodMutationVariables
  >(SetupRecurringPaymentMethodDocument, {})

  const stripePromise = useMemo(() => loadStripe(getSharedConfig().STRIPE_API_PUBLISHABLE_KEY), [])

  useEffect(() => {
    if (!orderData) return

    if (!orderData.order.payment && !orderData.order.isConfirmable) {
      throw new BlockingError('There was no payment method available', intl.noPaymentMethodAvailable())
    }
  }, [orderData, intl])

  useEffect(() => {
    if (paymentInProgress) {
      const storedSecret = sessionUtils.getSetupIntentClientSecret()
      if (storedSecret) {
        setClientSecret(storedSecret)
      }
    }
  }, [paymentInProgress])

  useEffect(() => {
    if (paymentInProgress) return
    if (!orderData?.order?.payment?.canSetupRecurringPaymentMethod) return

    const paymentId = orderData.order.payment.id
    const storedSecret = sessionUtils.getSetupIntentClientSecret()
    const needsRefetch = !storedSecret || clientSecret !== storedSecret

    if (lastFetchedPaymentId.current === paymentId && !needsRefetch) return

    lastFetchedPaymentId.current = paymentId
    setupRecurringPayment({
      variables: { paymentId },
    })
  }, [
    orderData?.order?.payment?.id,
    paymentInProgress,
    orderData?.order?.payment?.canSetupRecurringPaymentMethod,
    clientSecret,
  ])

  useEffect(() => {
    if (paymentInProgress) return
    if (!recurringPaymentData) return

    const serverSecret =
      recurringPaymentData.setupRecurringPaymentMethod?.__typename === 'Payment'
        ? recurringPaymentData.setupRecurringPaymentMethod.paymentSetupIntent?.clientSecret
        : undefined

    if (serverSecret) {
      sessionUtils.setSetupIntentClientSecret(serverSecret)
      setClientSecret(serverSecret)
    }
  }, [recurringPaymentData, paymentInProgress])

  if (
    isOrderLoading ||
    !orderData ||
    isSetupLoading ||
    (orderData?.order?.payment?.canSetupRecurringPaymentMethod && !clientSecret)
  ) {
    return <SkeletonPayment />
  }

  if (recurringPaymentData?.setupRecurringPaymentMethod.__typename === 'ExpectedErrors') {
    throw new Error(recurringPaymentData.setupRecurringPaymentMethod.errors.map((e) => e.message).join('\n'))
  }

  if (!orderData.order.payment && orderData.order.isConfirmable) {
    return <CashPayment />
  }

  const customPaymentMethods = []

  if (orderData.order.isConfirmable && getSharedConfig().OFFLINE_PAYMENT_CUSTOM_STRIPE_PAYMENT_METHOD_ID) {
    customPaymentMethods.push({
      id: getSharedConfig().OFFLINE_PAYMENT_CUSTOM_STRIPE_PAYMENT_METHOD_ID,
      options: { type: 'static', subtitle: intl.offlinePaymentSubtitle() },
    } as const)
  }

  const finalClientSecret = orderData.order.payment?.canSetupRecurringPaymentMethod
    ? clientSecret
    : orderData.order.payment?.paymentIntent?.clientSecret

  return (
    <PaymentScreen
      key={finalClientSecret}
      stripePromise={stripePromise}
      customPaymentMethods={customPaymentMethods}
      clientSecret={finalClientSecret}
      customerSessionClientSecret={orderData.order.payment?.customerSessionClientSecret ?? ''}
    />
  )
}

export default PaymentScreenWrapper
